// The phonics scope & sequence — the skill-tree the practice engine climbs.
// Dependency-ordered, mirroring docs/03 (Orton-Gillingham / UFLI canonical order).
// Each Level holds the Patterns taught at that level. The first four levels are
// fully specified (the seed); later levels are stubs to fill in Phase 6.

export type LevelId =
  | 'cvc'
  | 'digraphs'
  | 'blends'
  | 'vce'
  | 'vowel_teams'
  | 'r_controlled'
  | 'diphthongs';

export interface Pattern {
  id: string;
  level: LevelId;
  /** Kid/parent-facing label, e.g. "/sh/" or "short a". */
  label: string;
  /** The grapheme(s) this pattern teaches. */
  graphemes: string[];
  /** An example decodable word. */
  example: string;
}

export interface Level {
  id: LevelId;
  order: number;
  title: string;
  patterns: Pattern[];
  /** True for levels not yet authored (no content/patterns seeded). */
  stub?: boolean;
}

const p = (id: string, level: LevelId, label: string, graphemes: string[], example: string): Pattern => ({
  id,
  level,
  label,
  graphemes,
  example,
});

export const LEVELS: Level[] = [
  {
    id: 'cvc',
    order: 0,
    title: 'Short vowels (CVC)',
    patterns: [
      p('short_a', 'cvc', 'short a', ['a'], 'cat'),
      p('short_i', 'cvc', 'short i', ['i'], 'pig'),
      p('short_o', 'cvc', 'short o', ['o'], 'dog'),
      p('short_e', 'cvc', 'short e', ['e'], 'web'),
      p('short_u', 'cvc', 'short u', ['u'], 'cup'),
    ],
  },
  {
    id: 'digraphs',
    order: 1,
    title: 'Digraphs',
    patterns: [
      p('sh', 'digraphs', '/sh/', ['sh'], 'ship'),
      p('ch', 'digraphs', '/ch/', ['ch'], 'chin'),
      p('th', 'digraphs', '/th/', ['th'], 'moth'),
      p('wh', 'digraphs', '/wh/', ['wh'], 'whip'),
      p('ck', 'digraphs', '/ck/', ['ck'], 'duck'),
      p('ng', 'digraphs', '/ng/', ['ng'], 'ring'),
    ],
  },
  {
    id: 'blends',
    order: 2,
    title: 'Blends',
    patterns: [
      p('bl', 'blends', 'bl-', ['bl'], 'blot'),
      p('cl', 'blends', 'cl-', ['cl'], 'clap'),
      p('fl', 'blends', 'fl-', ['fl'], 'flag'),
      p('gr', 'blends', 'gr-', ['gr'], 'grab'),
      p('tr', 'blends', 'tr-', ['tr'], 'trip'),
      p('st', 'blends', '-st', ['st'], 'nest'),
      p('nd', 'blends', '-nd', ['nd'], 'sand'),
      p('mp', 'blends', '-mp', ['mp'], 'lamp'),
    ],
  },
  {
    id: 'vce',
    order: 3,
    title: 'Silent-e (magic e)',
    patterns: [
      p('a_e', 'vce', 'a_e', ['a', 'e'], 'cake'),
      p('i_e', 'vce', 'i_e', ['i', 'e'], 'bike'),
      p('o_e', 'vce', 'o_e', ['o', 'e'], 'home'),
      p('u_e', 'vce', 'u_e', ['u', 'e'], 'cube'),
    ],
  },
  {
    id: 'vowel_teams',
    order: 4,
    title: 'Vowel teams',
    patterns: [
      p('ai', 'vowel_teams', 'ai', ['ai'], 'rain'),
      p('ay', 'vowel_teams', 'ay', ['ay'], 'play'),
      p('ee', 'vowel_teams', 'ee', ['ee'], 'feet'),
      p('ea', 'vowel_teams', 'ea', ['ea'], 'seat'),
      p('oa', 'vowel_teams', 'oa', ['oa'], 'boat'),
      p('ow_o', 'vowel_teams', 'ow (snow)', ['ow'], 'snow'),
    ],
  },
  {
    id: 'r_controlled',
    order: 5,
    title: 'R-controlled vowels',
    patterns: [
      p('ar', 'r_controlled', 'ar', ['ar'], 'car'),
      p('or', 'r_controlled', 'or', ['or'], 'fork'),
      p('er', 'r_controlled', 'er', ['er'], 'fern'),
      p('ir', 'r_controlled', 'ir', ['ir'], 'bird'),
      p('ur', 'r_controlled', 'ur', ['ur'], 'surf'),
    ],
  },
  {
    id: 'diphthongs',
    order: 6,
    title: 'Diphthongs & more vowel teams',
    patterns: [
      p('oi', 'diphthongs', 'oi', ['oi'], 'coin'),
      p('oy', 'diphthongs', 'oy', ['oy'], 'boy'),
      p('ou', 'diphthongs', 'ou', ['ou'], 'cloud'),
      p('ow_ou', 'diphthongs', 'ow (cow)', ['ow'], 'cow'),
      p('oo_long', 'diphthongs', 'oo (moon)', ['oo'], 'moon'),
      p('oo_short', 'diphthongs', 'oo (book)', ['oo'], 'book'),
      p('aw', 'diphthongs', 'aw', ['aw'], 'claw'),
    ],
  },
];

export const ALL_PATTERNS: Pattern[] = LEVELS.flatMap((l) => l.patterns);

const PATTERN_BY_ID = new Map(ALL_PATTERNS.map((pat) => [pat.id, pat]));
export function patternById(id: string): Pattern | undefined {
  return PATTERN_BY_ID.get(id);
}

export function levelOrder(level: LevelId): number {
  return LEVELS.find((l) => l.id === level)?.order ?? 0;
}

/** All patterns taught at or before the given level (for decodability checks). */
export function patternsUpTo(level: LevelId): Pattern[] {
  const max = levelOrder(level);
  return ALL_PATTERNS.filter((pat) => levelOrder(pat.level) <= max);
}
